import {
  LeaveTracking,
  SupportingDocumentRule,
} from '@gokrew/models/leave-management';
/* eslint-disable @typescript-eslint/no-unused-expressions */

import {
  Meridiem,
  LeaveDuration,
  type FormattedEmployeeLeaveBalances,
  type AdminLeaveRequestFormValues,
  QuotaAddType,
  type AdminCreateLeaveRequestManagementPayload,
} from '@gokrew/models/leaves';
import { Button } from '@gokrew/ui';
import { Fragment } from 'react/jsx-runtime';
import {
  FormDropdown,
  FormField,
  FormikWithSchema,
  InfiniteComboBoxField,
} from '@gokrew/ui/form';
import { Form, useField, type FormikHelpers } from 'formik';
import { DatePickerField } from '@gokrew/ui/form/DatePickerField';
import type { DialogComponentProps } from '@gokrew/models/dialog';
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@gokrew/ui/lib/utils';
import {
  getFormFieldClasses,
  generateEnumOptions,
} from '@gokrew/ui/utility/utility';
import {
  FieldWrapper,
  type FieldWrapperConfig,
} from '@gokrew/ui/form/FieldWrapper';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@gokrew/ui/components/dropdown-menu';
import { useDialog } from '@gokrew/ui/context';
import { useInfiniteEmployees } from '@/hooks/employees/useEmployee';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useEmployeeWorkDayMinutes } from '@/hooks/leaves/useEmployeeWorkDayMinutes';
import { isHourlyExceedingWorkDay } from '@gokrew/ui/utility/hourlyLeave';
import { useEmployeeLeaveWallets } from '@/hooks/leaves/useEmployeeLeaveWallets';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import {
  createCompanyLeaveRequest,
  requestLeaveAttachmentUploadUrl,
} from '@/api/leave-management';
import {
  LeaveAttachmentsField,
  uploadLeaveAttachments,
} from '@gokrew/ui/components';
import { LeaveDatesPreview } from '@gokrew/ui/components/LeaveDatesPreview';
import toast from 'react-hot-toast';
import { companyRequestLeaveSchema } from '@/validation/leavesSchema';
import { useLeaveDayCount } from '@/hooks/leaves/useLeaveDayCount';
import { leaveDayCountParams } from '@gokrew/ui/utility/leaveDayCount';

export interface CompanyRequestLeaveFormProps extends DialogComponentProps {
  balances?: FormattedEmployeeLeaveBalances[];
  onNavigate?: (path: string) => void;
}

export function CompanyRequestLeaveForm({
  onClose,
  dialogId,
  onNavigate,
}: CompanyRequestLeaveFormProps) {
  const { closeDialog } = useDialog();
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation('leave-request-management');
  const { t: tLeave } = useTranslation(TranslationNS.LeaveManagement);

  const [employeeId, setEmployeeId] = useState<string | undefined>();

  const walletsQuery = useEmployeeLeaveWallets(
    {
      companyId: companyId!,
      employeeId: employeeId!,
    },
    {
      enabled: !!companyId && !!employeeId,
    }
  );

  const formattedWallets: FormattedEmployeeLeaveBalances[] =
    walletsQuery.data?.map((wallet) => ({
      id: wallet.id,
      totalAllocatedDays: wallet.available,
      usedDays: wallet.taken,
      available: wallet.available,
      totalAllocatedInHours: wallet.availableInHours,
      takenInHours: wallet.takenInHours,
      availableInHours: wallet.availableInHours,
      name: wallet.leaveType.name,
      minAdvanceDays: wallet.policyLeaveType?.minAdvanceDays ?? 0,
      isAutoApproved: wallet.policyLeaveType?.isAutoApproved ?? false,

      tracking: wallet.policyLeaveType?.tracking,

      isPaid: wallet.policyLeaveType?.isPaid,
      reasonRequired: wallet.policyLeaveType?.reasonRequired,
      employeeCanRequest: wallet.policyLeaveType?.employeeCanRequest,
      supportingDocument: wallet.policyLeaveType?.supportingDocument,
      description: wallet.leaveType.description ?? '',
      policyName: wallet.leavePolicy.name,
      quotaAddType: wallet.leaveType.quotaAddType ?? QuotaAddType.NONE,
      isActive: wallet.isActive,
      walletDisplayUnit: wallet.walletDisplayUnit,
    })) ?? [];

  const initialValues = useMemo((): Partial<AdminLeaveRequestFormValues> => {
    return {
      leaveDuration: LeaveDuration.FULL_DAY,
      reason: '',
      startTime: '',
      endTime: '',
      attachments: [],
    };
  }, []);

  const closeDialogHandler = () => {
    if (dialogId) closeDialog(dialogId);
  };

  const cancelHandler = () => {
    closeDialogHandler();
    onClose?.();
  };

  const handleNavigate = (path: string) => {
    closeDialogHandler();
    onClose?.();
    onNavigate?.(path);
  };

  const createMutation = useMutation({
    mutationFn: (payload: AdminCreateLeaveRequestManagementPayload) =>
      createCompanyLeaveRequest(companyId!, payload),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.LeaveRequestsManagement],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.LeaveRequestsSummary],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CompanyLeaveCalendar],
      });

      toast.success(t('request_form.toast.create'));
      dialogId && closeDialog(dialogId);
      onClose?.();
    },
  });

  const handleSubmit = async <T,>(
    values: Partial<AdminLeaveRequestFormValues>,
    { setSubmitting }: FormikHelpers<T>
  ) => {
    try {
      const payload = formatAdminPayload(values);
      // Supporting documents go to S3 first, under the employee's prefix;
      // a type that takes none sends none.
      const rule = formattedWallets.find(
        (wallet) => wallet.id === values.leaveWalletId
      )?.supportingDocument;
      const files =
        rule === SupportingDocumentRule.None ? [] : (values.attachments ?? []);
      if (files.length && companyId && values.employeeId) {
        const employee = values.employeeId;
        try {
          payload.attachments = await uploadLeaveAttachments(files, (meta) =>
            requestLeaveAttachmentUploadUrl(companyId, employee, meta)
          );
        } catch {
          toast.error(tLeave('leave_attachments.upload_error'));
          return;
        }
      }
      await createMutation.mutateAsync(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const { data: workHours } = useEmployeeWorkDayMinutes(companyId, employeeId);

  return (
    <FormikWithSchema
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={companyRequestLeaveSchema(t, formattedWallets)}
      validateOnChange
    >
      {(formik) => (
        <CompanyLeaveFormBody
          {...formik}
          wallets={formattedWallets}
          setEmployeeId={setEmployeeId}
          onCancel={cancelHandler}
          workDayMinutes={workHours?.workDayMinutes}
          onNavigate={handleNavigate}
        />
      )}
    </FormikWithSchema>
  );
}

/* ------------------------------------------------------------------------------------------------ */
/* Form Body */
/* ------------------------------------------------------------------------------------------------ */

interface FormBodyProps {
  values: Partial<AdminLeaveRequestFormValues>;
  isSubmitting: boolean;
  setFieldValue: (field: string, value: unknown) => void;
  wallets: FormattedEmployeeLeaveBalances[];
  setEmployeeId: (id?: string) => void;
  onCancel: () => void;
  isValid: boolean;
  workDayMinutes?: number;
  onNavigate?: (path: string) => void;
}

function CompanyLeaveFormBody({
  values,
  isSubmitting,
  setFieldValue,
  wallets,
  setEmployeeId,
  onCancel,
  isValid,
  workDayMinutes,
  onNavigate,
}: FormBodyProps) {
  const { t } = useTranslation('leave-request-management');
  const { companyId } = useAuth();
  const isEmployeeSelected = !!values.employeeId;
  // The server counts the days for this employee (their branch holidays on
  // the observed day), so the preview equals what the request will deduct.
  const { data: dayCount } = useLeaveDayCount(
    companyId,
    values.employeeId,
    leaveDayCountParams(values)
  );

  const hourlyExceedsWorkDay =
    values.leaveDuration === LeaveDuration.HOURLY &&
    isHourlyExceedingWorkDay(values.startTime, values.endTime, workDayMinutes);
  useEffect(() => {
    setEmployeeId(values.employeeId);
    setFieldValue('leaveWalletId', undefined);
    // Files are uploaded under the employee picked: start over on a change.
    setFieldValue('attachments', []);
  }, [values.employeeId]);
  const supportingDocument = wallets.find(
    (wallet) => wallet.id === values.leaveWalletId
  )?.supportingDocument;

  return (
    <Form className="grid grid-cols-2 gap-x-4">
      <InfiniteComboBoxField
        name="employeeId"
        fieldClassName="col-span-full"
        label={t('request_form.form.fields.employee.label')}
        useHook={useInfiniteEmployees}
        searchParamKey="search"
        baseParams={{ limit: 99 }}
        mapOptions={(data) =>
          data?.data?.data?.map((emp) => ({
            value: emp.id,
            label: `${emp.user.firstName} ${emp.user.lastName}`,
          })) ?? []
        }
      />

      <LeaveTypeDropdownInline
        name="leaveWalletId"
        balances={wallets}
        disabled={!isEmployeeSelected}
        label={t('request_form.form.fields.leave_type.label')}
        placeholder={t('request_form.form.fields.leave_type.placeholder')}
        onNavigate={onNavigate}
      />

      <FormDropdown
        name="leaveDuration"
        label={t('request_form.form.fields.leave_duration.label')}
        options={generateEnumOptions(
          LeaveDuration,
          'request_form.leave_duration',
          t
        )}
        disabled={!isEmployeeSelected}
      />

      <LeaveDurationFields
        duration={values.leaveDuration}
        disabled={!isEmployeeSelected}
      />

      <FormField
        name="reason"
        fieldClassName="col-span-full"
        label={
          wallets.find((w) => w.id === values.leaveWalletId)?.reasonRequired
            ? `${t('request_form.form.fields.reason.label')} *`
            : t('request_form.form.fields.reason.label')
        }
        placeholder={t('request_form.form.fields.reason.placeholder')}
        disabled={!isEmployeeSelected}
      />

      {values.leaveWalletId && (
        <div className="col-span-full">
          <LeaveAttachmentsField
            name="attachments"
            rule={supportingDocument}
            disabled={!isEmployeeSelected}
          />
        </div>
      )}

      <div className="flex col-span-full justify-end gap-2 mt-2">
        <div className="flex-1">
          <LeaveDatesPreview
            leaveDuration={values.leaveDuration}
            leaveDate={values.leaveDate}
            startDate={values.startDate}
            endDate={values.endDate}
            meridiem={values.meridiem}
            dayCount={dayCount}
            startTime={values.startTime}
            endTime={values.endTime}
            workDayMinutes={workDayMinutes}
          />
        </div>
        <div className="flex gap-2 ">
          <Button variant="outline" type="button" onClick={onCancel}>
            {t('request_form.buttons.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              !isEmployeeSelected ||
              !isValid ||
              hourlyExceedsWorkDay
            }
          >
            {isSubmitting
              ? t('request_form.buttons.requesting')
              : t('request_form.buttons.request')}
          </Button>
        </div>
      </div>
    </Form>
  );
}

// -----------------------------------------------------------------------------------------------------
// Local Components
// -----------------------------------------------------------------------------------------------------

function LeaveDurationFields({
  duration = LeaveDuration.FULL_DAY,
  disabled,
}: {
  duration?: LeaveDuration;
  disabled?: boolean;
}) {
  const { t } = useTranslation('leave-request-management');

  switch (duration) {
    case LeaveDuration.FULL_DAY:
    default:
      return <LeaveDate spanFull disabled={disabled} />;

    case LeaveDuration.HALF_DAY:
      return (
        <Fragment>
          <LeaveDate disabled={disabled} />
          <FormDropdown
            label={t('request_form.form.fields.am_pm.label')}
            placeholder={t('request_form.form.fields.am_pm.placeholder')}
            name="meridiem"
            options={generateEnumOptions(
              Meridiem,
              'request_form.form.fields.am_pm.options',
              t
            )}
            disabled={disabled}
          />
        </Fragment>
      );

    case LeaveDuration.MULTI_DAY:
      return (
        <Fragment>
          <DatePickerField
            disabled={disabled}
            name="startDate"
            label={t('request_form.form.fields.start_date.label')}
          />
          <DatePickerField
            disabled={disabled}
            name="endDate"
            label={t('request_form.form.fields.end_date.label')}
          />
        </Fragment>
      );

    case LeaveDuration.HOURLY:
      return <HourlyLeaveFields disabled={disabled} />;
  }
}

function HourlyLeaveFields({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslation('leave-request-management');

  return (
    <Fragment>
      <LeaveDate spanFull disabled={disabled} />
      <FormField
        type="time"
        name="startTime"
        label={t('request_form.form.fields.start_time.label')}
        disabled={disabled}
      />
      <FormField
        type="time"
        name="endTime"
        label={t('request_form.form.fields.end_time.label')}
        disabled={disabled}
      />
    </Fragment>
  );
}

function LeaveDate({
  spanFull,
  disabled,
}: {
  spanFull?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation('leave-request-management');
  const className = spanFull ? 'col-span-full' : '';

  return (
    <DatePickerField
      disabled={disabled}
      name="leaveDate"
      fieldClassName={className}
      label={t('request_form.form.fields.leave_date.label')}
    />
  );
}

// -----------------------------------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------------------------------

function LeaveTypeDropdownInline({
  name,
  balances,
  placeholder = 'Select',
  disabled,
  onNavigate,
  ...wrapperProps
}: FieldWrapperConfig & {
  balances: FormattedEmployeeLeaveBalances[];
  placeholder?: string;
  disabled?: boolean;
  onNavigate?: (path: string) => void;
}) {
  const [field, meta, helpers] = useField(name);
  const selectedBalance = balances.find((b) => b.id === field.value);
  const { t } = useTranslation('leave-request-management');

  const getBalanceDetails = ({
    usedDays,
    available,
    tracking,
  }: FormattedEmployeeLeaveBalances) => {
    // A record-only absence has no balance to show (spec 2026-10-21).
    if (tracking === LeaveTracking.RecordOnly)
      return t('request_form.form.fields.leave_type.record_only');
    const remainingDays = available - usedDays;
    return remainingDays.toFixed(2);
  };

  let selectedLabel = placeholder;
  if (selectedBalance) {
    selectedLabel = `${selectedBalance.name} (${getBalanceDetails(selectedBalance)})`;
  }

  return (
    <FieldWrapper name={name} errorMeta={meta} {...wrapperProps}>
      <DropdownMenu
        onOpenChange={(open) => {
          if (!disabled && !open) helpers.setTouched(true);
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={getFormFieldClasses({
              meta,
              className: cn(
                'w-full justify-between flex px-4 py-3 border-gray-300 border rounded-lg',
                disabled &&
                  'bg-gray-100 border-gray-300 text-gray-400 placeholder:text-gray-400 cursor-not-allowed'
              ),
            })}
          >
            <span className="truncate">{selectedLabel}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-full">
          {balances.map((balance) => (
            <DropdownMenuItem
              key={balance.id}
              onClick={() => {
                helpers.setValue(balance.id);
                helpers.setTouched(true);
              }}
            >
              {`${balance.name} (${getBalanceDetails(balance)})`}
            </DropdownMenuItem>
          ))}
          {balances.length === 0 && (
            <div className="px-3 py-4 text-center space-y-2">
              <p className="text-xs text-gray-500">
                {t('request_form.form.fields.leave_type.no_leaves')}
              </p>
              <Button
                className="text-xs text-blue-600 font-medium hover:bg-transparent p-0 h-auto"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate?.('/leave-management/policies');
                }}
              >
                {t('request_form.form.fields.leave_type.setup_policy')}
              </Button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </FieldWrapper>
  );
}

function formatAdminPayload(
  formData: Partial<AdminLeaveRequestFormValues>
): AdminCreateLeaveRequestManagementPayload {
  const payload = {
    employeeId: formData.employeeId!,
    leaveWalletId: formData.leaveWalletId!,
    reason: formData.reason,
    startSession: Meridiem.AM,
    endSession: Meridiem.PM,
  } as AdminCreateLeaveRequestManagementPayload;

  const formatDate = (d?: string | Date) =>
    typeof d === 'string' ? d : (d?.toISOString().split('T')[0] ?? '');

  switch (formData.leaveDuration) {
    case LeaveDuration.HALF_DAY: {
      const date = formatDate(formData.leaveDate);
      payload.startDate = date;
      payload.endDate = date;
      if (formData.meridiem === Meridiem.AM) {
        payload.endSession = Meridiem.AM;
      } else {
        payload.startSession = Meridiem.PM;
      }
      break;
    }

    case LeaveDuration.MULTI_DAY:
      payload.startDate = formatDate(formData.startDate);
      payload.endDate = formatDate(formData.endDate);
      break;

    case LeaveDuration.HOURLY: {
      const date = formatDate(formData.leaveDate);
      payload.startDate = date;
      payload.endDate = date;
      payload.startTime = formData.startTime;
      payload.endTime = formData.endTime;
      delete payload.startSession;
      delete payload.endSession;
      break;
    }

    case LeaveDuration.FULL_DAY:
    default: {
      const date = formatDate(formData.leaveDate);
      payload.startDate = date;
      payload.endDate = date;
    }
  }

  return payload;
}
